import firebase from '@/firebase'
import store from '@/store/store'

const functions = firebase.functions

// The callable this service reports through. Exported so firebaseFunctionsInterceptor can
// recognise it and leave its failures alone - see the comment there.
export const ERROR_LOG_FUNCTION_NAME = 'errorLogs-logError'

class ErrorLogger {
  constructor () {
    this.loggedErrors = new Set()
    this.rateLimitWindow = 60000 // 1 minute
    this.maxErrorsPerWindow = 10
  }

  /**
   * Log an error to Firestore
   * @param {Error} error - The error object
   * @param {Object} context - Additional context about the error
   * @param {string} errorType - Type of error ('javascript', 'vue', 'promise', 'manual')
   */
  async logError (error, context = {}, errorType = 'manual') {
    try {
      // Rate limiting - prevent spam
      const errorKey = this.generateErrorKey(error, errorType)
      if (this.isDuplicateError(errorKey)) {
        return
      }

      // Claim the key BEFORE the call rather than after it succeeds. A report that FAILS is
      // precisely the one that must not be retried in a loop, and recording the key only on
      // success left the failing path with no brake at all. Claimed here, an error that cannot
      // be reported is attempted once per rate-limit window and no more.
      this.loggedErrors.add(errorKey)
      setTimeout(() => {
        this.loggedErrors.delete(errorKey)
      }, this.rateLimitWindow)

      const errorData = this.formatErrorData(error, context, errorType)

      // Call Firebase function to log the error
      const logErrorFunction = functions.httpsCallable(ERROR_LOG_FUNCTION_NAME)
      await logErrorFunction(errorData)
    } catch (logError) {
      // Fallback - don't let error logging break the app
      console.error('Failed to log error:', logError)
      console.error('Original error:', error)

      // Additional fallback for CORS or network issues
      if (logError.code === 'functions/internal' || (logError.message && logError.message.includes('CORS'))) {
        console.warn('Error logging service unavailable - this may be due to CORS policy or function deployment issues')
      }
    }
  }

  /**
   * Format error data for logging
   */
  formatErrorData (error, context, errorType) {
    const user = store.getters['auth/authUser']

    return {
      errorType,
      message: error.message || 'Unknown error',
      stack: error.stack || null,
      name: error.name || 'Error',
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: (user && user.uid) || null,
      userEmail: (user && user.email) || null,
      route: (this.$route && this.$route.name) || this.getCurrentRoute(),
      context: {
        ...context,
        browserInfo: this.getBrowserInfo(),
        viewport: this.getViewportInfo()
      },
      resolved: false
    }
  }

  /**
   * Generate a unique key for error deduplication
   */
  generateErrorKey (error, errorType) {
    const message = error.message || 'unknown'
    const line = error.lineno || error.lineNumber || 0
    const file = error.filename || error.fileName || 'unknown'
    return `${errorType}-${message}-${file}-${line}`.slice(0, 100)
  }

  /**
   * Check if this error was already logged recently
   */
  isDuplicateError (errorKey) {
    return this.loggedErrors.has(errorKey)
  }

  /**
   * Get current route information
   */
  getCurrentRoute () {
    try {
      return window.location.pathname
    } catch (e) {
      return 'unknown'
    }
  }

  /**
   * Get browser information
   */
  getBrowserInfo () {
    return {
      language: navigator.language,
      platform: navigator.userAgentData ? navigator.userAgentData.platform : navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine
    }
  }

  /**
   * Get viewport information
   */
  getViewportInfo () {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      screenWidth: screen.width,
      screenHeight: screen.height
    }
  }

  /**
   * Manual error logging method for use in catch blocks
   */
  log (error, context = {}) {
    this.logError(error, context, 'manual')
  }

  /**
   * Log warning (non-error issues)
   */
  warn (message, context = {}) {
    const warningError = new Error(message)
    warningError.name = 'Warning'
    this.logError(warningError, context, 'warning')
  }

  /**
   * Log info (for debugging purposes)
   */
  info (message, context = {}) {
    const infoError = new Error(message)
    infoError.name = 'Info'
    this.logError(infoError, context, 'info')
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger()

// Export singleton instance
export default errorLogger
